import {
  type DashboardExperienceOrderResponse,
  type DashboardExperienceOrderSkipDiscountResponse,
} from '@library/api-dashboard-experience'
import { queueTrackingEvent } from '@library/tracking-integration'

type TrackHomeOrdersTabClickParams = {
  order: DashboardExperienceOrderResponse
  tabPosition: number
}

type TrackGetHelpWithThisBoxClickParams = {
  orderId: string
}

type TrackOrderExpandClickParams = {
  order: DashboardExperienceOrderResponse
}

type TrackChooseRecipesClickParams = {
  order: DashboardExperienceOrderResponse
}

type TrackEditRecipesClickParams = {
  order: DashboardExperienceOrderResponse
}

type TrackRestoreOrderClickParams = {
  order: DashboardExperienceOrderResponse
}

type TrackCancelOrderClickParams = {
  order: DashboardExperienceOrderResponse
}

type TrackOrderKeptParams = {
  order: DashboardExperienceOrderResponse
  skipDiscountOffer: DashboardExperienceOrderSkipDiscountResponse | null
}

type TrackOrderSkipCompleteParams = {
  order: DashboardExperienceOrderResponse
  skipDiscountOffer: DashboardExperienceOrderSkipDiscountResponse | null
}

type TrackSaveDeliveryDateParams = {
  newDeliverySlotId: string
  order: DashboardExperienceOrderResponse
}

type TrackMyOrderClickParams = {
  order: DashboardExperienceOrderResponse
}

type TrackRecommendedRecipeClickParams = {
  orderId: string
  collection?: 'top picks for you' | 'new and limited edition'
}

type TrackCarouselScrollParams = {
  orderId: string
  collection: 'top picks for you' | 'new and limited edition'
}

type TrackCarouselSeeMoreClickParams = {
  orderId: string
  collection: 'top picks for you' | 'new and limited edition'
}

type TrackingEventsForOrderTabClick = {
  action: 'click_home_orders_tab'
  properties: {
    delivery_day_id: string
    phase: string
    order_state: string
    tab_position: number
    delivery_date: string
    order_id: string
    delivery_slot_id: string
    period_id: string
    portion_size: string
  }
}

type TrackingEventsForGetHelpWithThisBoxClick = {
  action: 'click_get_help_with_this_box'
  properties: {
    order_id: string
  }
}

type TrackingEventsForOrderExpandClick = {
  action: 'order_expand_click'
  properties: {
    order_id: string
    period_id: string
    portion_size: string
    delivery_date: string
    order_state: string
  }
}

type TrackingEventsForChooseRecipesClick = {
  action: 'choose_recipes_click'
  properties: {
    order_id: string
    period_id: string
    portion_size: string
    delivery_date: string
    order_state: string
  }
}

type TrackingEventsForEditRecipesClick = {
  action: 'click_edit_recipes'
  properties: {
    order_id: string
    period_id: string
    portion_size: string
    delivery_date: string
    order_state: string
  }
}

type TrackingEventsForRestoreOrderClick = {
  action: 'restore_order_click'
  properties: {
    order_id: string
    period_id: string
    portion_size: string
    delivery_date: string
    order_state: string
  }
}

type TrackingEventsForCancelOrderClick = {
  action: 'cancel_order_click'
  properties: {
    order_id: string
    period_id: string
    portion_size: string
    delivery_date: string
    order_state: string
  }
}

type RecoveryReasons = [string | null, DashboardExperienceOrderSkipDiscountResponse | null]

type TrackingEventsForOrderKept = {
  action: 'Order Kept'
  properties: {
    order_id: string
    delivery_day_id: string
    order_state: string
    recovery_reasons: RecoveryReasons
  }
}

type TrackingEventsForOrderSkipComplete = {
  action: 'Order Cancelled' | 'Order Skipped'
  properties: {
    order_id: string
    delivery_day_id: string
    order_state: string
    recovery_reasons: RecoveryReasons
  }
}

type TrackingEventsForSaveDeliveryDate = {
  action: 'OrderDeliverySlot Saved'
  properties: {
    order_id: string
    original_deliveryslot_id: string
    new_deliveryslot_id: string
  }
}

type TrackingEventsForTrackMyOrderClick = {
  action: 'track_my_order'
  properties: {
    phase: string
    order_state: string
    delivery_tracking_available: boolean
    period_id: string
    portion_size: string
    order_id: string
    delivery_day_id: string
    delivery_slot_id: string
    delivery_date: string
  }
}

type TrackingEventsForRecommendedRecipeClick = {
  action: 'click_recommended_recipe'
  properties: {
    order_id: string
    collection?: 'top picks for you' | 'new and limited edition'
  }
}

type TrackingEventsForCarouselScroll = {
  action: 'carousel_scroll'
  properties: {
    order_id: string
    collection: 'top picks for you' | 'new and limited edition'
  }
}

type TrackingEventsForCarouselSeeMoreClick = {
  action: 'click_carousel_see_more'
  properties: {
    order_id: string
    collection: 'top picks for you' | 'new and limited edition'
  }
}

export type UseOrderTracking = {
  trackHomeOrdersTabClick: (params: TrackHomeOrdersTabClickParams) => void
  trackGetHelpWithThisBoxClick: (params: TrackGetHelpWithThisBoxClickParams) => void
  trackOrderExpandClick: (params: TrackOrderExpandClickParams) => void
  trackChooseRecipesClick: (params: TrackChooseRecipesClickParams) => void
  trackEditRecipesClick: (params: TrackEditRecipesClickParams) => void
  trackRestoreOrderClick: (params: TrackRestoreOrderClickParams) => void
  trackCancelOrderClick: (params: TrackCancelOrderClickParams) => void
  trackOrderKept: (params: TrackOrderKeptParams) => void
  trackOrderSkipComplete: (params: TrackOrderSkipCompleteParams) => void
  trackSaveDeliveryDate: (params: TrackSaveDeliveryDateParams) => void
  trackMyOrderClick: (params: TrackMyOrderClickParams) => void
  trackRecommendedRecipeClick: (params: TrackRecommendedRecipeClickParams) => void
  trackCarouselScroll: (params: TrackCarouselScrollParams) => void
  trackCarouselSeeMoreClick: (params: TrackCarouselSeeMoreClickParams) => void
}

const getRecoveryReasons = (
  skipDiscountOffer: DashboardExperienceOrderSkipDiscountResponse | null,
): RecoveryReasons => [skipDiscountOffer?.subtitle ?? null, skipDiscountOffer]

const getOrderSkipCompleteAction = (
  orderState: string,
): TrackingEventsForOrderSkipComplete['action'] | null => {
  if (orderState === 'pending') {
    return 'Order Cancelled'
  }

  if (orderState === 'projected') {
    return 'Order Skipped'
  }

  return null
}

export const useOrderTracking = (): UseOrderTracking => {
  return {
    trackHomeOrdersTabClick: ({ order, tabPosition }: TrackHomeOrdersTabClickParams) => {
      queueTrackingEvent<TrackingEventsForOrderTabClick>({
        action: 'click_home_orders_tab',
        properties: {
          delivery_day_id: order.analytics.delivery_day_id,
          phase: order.analytics.order_phase,
          order_state: order.analytics.order_state,
          tab_position: tabPosition,
          delivery_date: order.analytics.delivery_date,
          order_id: order.analytics.order_id,
          delivery_slot_id: order.analytics.delivery_slot_id,
          period_id: order.analytics.period_id,
          portion_size: order.analytics.portion_size,
        },
      })
    },
    trackGetHelpWithThisBoxClick: ({ orderId }: TrackGetHelpWithThisBoxClickParams) => {
      queueTrackingEvent<TrackingEventsForGetHelpWithThisBoxClick>({
        action: 'click_get_help_with_this_box',
        properties: {
          order_id: orderId,
        },
      })
    },
    trackOrderExpandClick: ({ order }: TrackOrderExpandClickParams) => {
      queueTrackingEvent<TrackingEventsForOrderExpandClick>({
        action: 'order_expand_click',
        properties: {
          order_id: order.analytics.order_id,
          period_id: order.analytics.period_id,
          portion_size: order.analytics.portion_size,
          delivery_date: order.analytics.delivery_date,
          order_state: order.analytics.order_state,
        },
      })
    },
    trackChooseRecipesClick: ({ order }: TrackChooseRecipesClickParams) => {
      queueTrackingEvent<TrackingEventsForChooseRecipesClick>({
        action: 'choose_recipes_click',
        properties: {
          order_id: order.analytics.order_id,
          period_id: order.analytics.period_id,
          portion_size: order.analytics.portion_size,
          delivery_date: order.analytics.delivery_date,
          order_state: order.analytics.order_state,
        },
      })
    },
    trackEditRecipesClick: ({ order }: TrackEditRecipesClickParams) => {
      queueTrackingEvent<TrackingEventsForEditRecipesClick>({
        action: 'click_edit_recipes',
        properties: {
          order_id: order.analytics.order_id,
          period_id: order.analytics.period_id,
          portion_size: order.analytics.portion_size,
          delivery_date: order.analytics.delivery_date,
          order_state: order.analytics.order_state,
        },
      })
    },
    trackRestoreOrderClick: ({ order }: TrackRestoreOrderClickParams) => {
      queueTrackingEvent<TrackingEventsForRestoreOrderClick>({
        action: 'restore_order_click',
        properties: {
          order_id: order.analytics.order_id,
          period_id: order.analytics.period_id,
          portion_size: order.analytics.portion_size,
          delivery_date: order.analytics.delivery_date,
          order_state: order.analytics.order_state,
        },
      })
    },
    trackCancelOrderClick: ({ order }: TrackCancelOrderClickParams) => {
      queueTrackingEvent<TrackingEventsForCancelOrderClick>({
        action: 'cancel_order_click',
        properties: {
          order_id: order.analytics.order_id,
          period_id: order.analytics.period_id,
          portion_size: order.analytics.portion_size,
          delivery_date: order.analytics.delivery_date,
          order_state: order.analytics.order_state,
        },
      })
    },
    trackOrderKept: ({ order, skipDiscountOffer }: TrackOrderKeptParams) => {
      const trackingEvent: TrackingEventsForOrderKept = {
        action: 'Order Kept',
        properties: {
          order_id: order.analytics.order_id,
          delivery_day_id: order.analytics.delivery_day_id,
          order_state: order.analytics.order_state,
          recovery_reasons: getRecoveryReasons(skipDiscountOffer),
        },
      }

      queueTrackingEvent<TrackingEventsForOrderKept>(trackingEvent)
    },
    trackOrderSkipComplete: ({ order, skipDiscountOffer }: TrackOrderSkipCompleteParams) => {
      const action = getOrderSkipCompleteAction(order.analytics.order_state)

      if (!action) {
        return
      }

      const trackingEvent: TrackingEventsForOrderSkipComplete = {
        action,
        properties: {
          order_id: order.analytics.order_id,
          delivery_day_id: order.analytics.delivery_day_id,
          order_state: order.analytics.order_state,
          recovery_reasons: getRecoveryReasons(skipDiscountOffer),
        },
      }

      queueTrackingEvent<TrackingEventsForOrderSkipComplete>(trackingEvent)
    },
    trackSaveDeliveryDate: ({ newDeliverySlotId, order }: TrackSaveDeliveryDateParams) => {
      queueTrackingEvent<TrackingEventsForSaveDeliveryDate>({
        action: 'OrderDeliverySlot Saved',
        properties: {
          order_id: order.analytics.order_id,
          original_deliveryslot_id: order.analytics.delivery_slot_id,
          new_deliveryslot_id: newDeliverySlotId,
        },
      })
    },
    trackMyOrderClick: ({ order }: TrackMyOrderClickParams) => {
      queueTrackingEvent<TrackingEventsForTrackMyOrderClick>({
        action: 'track_my_order',
        properties: {
          phase: order.analytics.order_phase,
          order_state: order.analytics.order_state,
          delivery_tracking_available: true,
          period_id: order.analytics.period_id,
          portion_size: order.analytics.portion_size,
          order_id: order.analytics.order_id,
          delivery_day_id: order.analytics.delivery_day_id,
          delivery_slot_id: order.analytics.delivery_slot_id,
          delivery_date: order.analytics.delivery_date,
        },
      })
    },
    trackRecommendedRecipeClick: ({ orderId, collection }: TrackRecommendedRecipeClickParams) => {
      queueTrackingEvent<TrackingEventsForRecommendedRecipeClick>({
        action: 'click_recommended_recipe',
        properties: {
          order_id: orderId,
          ...(collection ? { collection } : {}),
        },
      })
    },
    trackCarouselScroll: ({ orderId, collection }: TrackCarouselScrollParams) => {
      queueTrackingEvent<TrackingEventsForCarouselScroll>({
        action: 'carousel_scroll',
        properties: {
          order_id: orderId,
          collection,
        },
      })
    },
    trackCarouselSeeMoreClick: ({ orderId, collection }: TrackCarouselSeeMoreClickParams) => {
      queueTrackingEvent<TrackingEventsForCarouselSeeMoreClick>({
        action: 'click_carousel_see_more',
        properties: {
          order_id: orderId,
          collection,
        },
      })
    },
  }
}
